// ============================================================
// Intake Component — V1.2
// ============================================================
// Extends V1.1's Intake.js. The single "Career Stage" dropdown
// (Individual Contributor / Manager / Director / VP) is REPLACED
// with the same 2-field pattern built for Pro:
//   1. Track — Individual Contributor or People Manager
//   2. Experience — a 5-band years dropdown (same bands, same
//      tie-break helper text, same reasoning as Pro)
// This is what actually drives which 20 of the 100 V1.2 questions
// get served — different bands see genuinely different content.
// Everything else (payment, OTP, pricing) is unchanged from V1.1.
// ============================================================

import React, { useState, useEffect } from 'react';
import OtpGate from './OtpGate';
import { getCurrentPrice, createPaymentOrder, confirmPayment, startSession } from '../services/api';
import { INDUSTRY_OPTIONS, ORIGIN_COMPANY_TYPES } from '../data/industryTaxonomy';

function Intake({ onSessionStarted }) {
  const [careerTrack, setCareerTrack] = useState('');
  const [experienceBand, setExperienceBand] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [industry, setIndustry] = useState('');
  // originCompanyType is kept as an always-null constant, not a
  // separate field anymore — "Where are you coming from?" is
  // removed as of v1.2.1 (folded into the Industry dropdown's
  // "Company Type" group instead). Deliberately NOT removed from
  // startSession's argument list — changing that argument count is
  // exactly the class of bug that caused real deployment problems
  // earlier, so "always pass null" is the safer change here.
  const originCompanyType = null;
  const [gccStatus, setGccStatus] = useState('aspiring');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCurrentPrice().then(setPriceInfo).catch(() => {});
  }, []);

  function loadRazorpayScript() {
    return new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  async function handlePayAndStart(e) {
    e.preventDefault();
    if (!careerTrack || !experienceBand || !industryType || !industry || !name) {
      setError('Please complete all required fields.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Could not load payment gateway. Please check your connection and try again.');
        setLoading(false);
        return;
      }

      const userId = 'user_' + Date.now();
      const order = await createPaymentOrder(userId);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'CHART Rubric',
        description: `${order.priceType === 'launch' ? 'Launch price' : 'Standard price'} — CHART Rubric Report`,
        order_id: order.orderId,
        prefill: { name, email: verifiedEmail, contact: phone || '' },
        handler: async function (response) {
          const result = await confirmPayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );

          if (!result.success) {
            setError('Payment verification failed. Please contact support if you were charged.');
            setLoading(false);
            return;
          }

          try {
            const session = await startSession(
              userId, experienceBand, careerTrack, industryType, industry, originCompanyType, gccStatus, order.orderId, name, verifiedEmail, phone
            );
            onSessionStarted(session.sessionId, name, verifiedEmail);
          } catch (err) {
            setError(err.message || 'Payment succeeded but we could not start your assessment. Please contact connect@sarthiedge.com.');
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: function () { setLoading(false); } },
        theme: { color: '#1a1a2e' }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError('Something went wrong starting the payment. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="intake">
      <h1>CHART Rubric</h1>
      <p className="descriptor">A rigorous behavioral assessment for GCC aspirants and professionals</p>

      {priceInfo && (
        <div className="pricing-banner">
          <strong>₹{priceInfo.amount}</strong>{' '}
          {priceInfo.priceType === 'launch' && (
            <span className="launch-note">Launch price — {priceInfo.remaining} slots left</span>
          )}
        </div>
      )}

      <div className="experience-note">
        <p>CHART Rubric works best for those with 4+ years of experience. If you're earlier in your career, this alternative might suit you better.</p>
        <a href="https://www.sarthiedge.com/sarthi-professionals" target="_blank" rel="noopener noreferrer">
          <button type="button" className="btn-secondary experience-redirect-btn">Explore Sarthi Professionals</button>
        </a>
      </div>

      <label className="field">Where are you right now?</label>
      <div className="radio-group">
        <label className="radio-option">
          <input
            type="radio"
            name="gccStatus"
            checked={gccStatus === 'aspiring'}
            onChange={() => setGccStatus('aspiring')}
          />
          I'm aspiring to get into a GCC
        </label>
        <label className="radio-option">
          <input
            type="radio"
            name="gccStatus"
            checked={gccStatus === 'within_gcc'}
            onChange={() => setGccStatus('within_gcc')}
          />
          I'm within a GCC, but trying to move to a different or better role, or currently facing some hurdles and want to understand where I stand
        </label>
      </div>

      {/* REPLACES the old single "Career Stage" dropdown — same
          Track + Experience pattern as CHART Rubric Pro, since job
          title alone doesn't reliably signal either years of
          experience or people-management responsibility.
          Career Track and "What type of role do you perform?" sit
          side by side as radio groups to save vertical space.

          The clarifying hint under the second field only shows when
          Non-IT is selected — that's the one case someone might
          pick wrong (e.g. HR at a tech company defaulting to "IT"
          because their company is IT). If IT is selected, or
          nothing's selected yet, the hint stays hidden — no need to
          explain a choice that isn't ambiguous. */}
      <div className="field-row">
        <div className="field-col">
          <label className="field">Career Track</label>
          <label className="radio-option compact">
            <input type="radio" name="careerTrack" checked={careerTrack === 'IC'} onChange={() => setCareerTrack('IC')} />
            Individual Contributor
          </label>
          <label className="radio-option compact">
            <input type="radio" name="careerTrack" checked={careerTrack === 'Manager'} onChange={() => setCareerTrack('Manager')} />
            People Manager
          </label>
        </div>

        <div className="field-col">
          <label className="field">What type of role do you perform?</label>
          <label className="radio-option compact">
            <input type="radio" name="industryType" checked={industryType === 'IT'} onChange={() => setIndustryType('IT')} />
            IT / Technical role
          </label>
          <label className="radio-option compact">
            <input type="radio" name="industryType" checked={industryType === 'Non_IT'} onChange={() => setIndustryType('Non_IT')} />
            Non-IT / Business role
          </label>
          {industryType === 'Non_IT' && (
            <span className="field-hint">Based on your actual function, not your company's industry — e.g. HR, Finance, or Ops roles at a tech company should select Non-IT.</span>
          )}
        </div>
      </div>

      <label className="field">
        Years of professional experience
        <select value={experienceBand} onChange={e => setExperienceBand(e.target.value)}>
          <option value="">Select...</option>
          <option value="4-8">4–8 years</option>
          <option value="8-12">8+–12 years</option>
          <option value="12-16">12+–16 years</option>
          <option value="16-20">16+–20 years</option>
          <option value="20+">20+ years</option>
        </select>
        <span className="field-hint">If your experience lands exactly on a boundary (e.g. exactly 8, 12, 16, or 20 years), choose the higher range.</span>
      </label>

      {/* v1.2.1: merged into ONE dropdown, two labeled groups —
          replaces the old separate "Industry" (filtered by Role
          Type) and standalone "Where are you coming from?" fields.
          Not filtered by Role Type anymore. BFSI is split into 4
          more specific display labels (Banking/Financial
          Services/Insurance/NBFC-Fintech) that all map to the same
          underlying 'BFSI' value in industryTaxonomy.js — same
          report content applies regardless of which of the 4
          someone picks. */}
      <label className="field">
        Industry
        <select value={industry} onChange={e => setIndustry(e.target.value)}>
          <option value="">Select...</option>
          <optgroup label="By Domain/Industry">
            {INDUSTRY_OPTIONS.map((opt, idx) => (
              <option key={`ind-${idx}`} value={opt.value}>{opt.label}</option>
            ))}
          </optgroup>
          <optgroup label="By Company Type">
            {ORIGIN_COMPANY_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </optgroup>
        </select>
      </label>

      <label className="field">
        Full Name
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
      </label>

      <label className="field">
        Mobile Number <span className="optional-tag">(optional)</span>
        <input type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91..." />
      </label>

      {!verifiedEmail ? (
        <>
          <label className="field">Email (required — your report will be sent here)</label>
          <OtpGate purpose="paid" onVerified={setVerifiedEmail} />
        </>
      ) : (
        <>
          <div className="verified-email-banner">✓ Verified: {verifiedEmail}</div>
          {error && <p className="error">{error}</p>}
          <button className="btn-primary" onClick={handlePayAndStart} disabled={loading}>
            {loading ? 'Processing...' : 'Pay & Begin Assessment'}
          </button>
          <p className="secure-payment-note">🔒 Pay safely and securely using Razorpay</p>
        </>
      )}

      <p className="privacy-note">We store your name, email, and phone (if given) only to deliver your report and occasionally follow up — never shared with third parties.</p>
    </div>
  );
}

export default Intake;
